import { useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { TOpenModalComprador } from "..";
import { TCompradoresEstoque } from "../../../../../../types/api/estoque/compradores-estoque";
import { formatarTelefone } from "../../../../../../helpers/masks";

export default function DialogCriacaoEditCompradores({
  open,
  handleClose,
  defaultValue,
}: DialogCriacaoEditCompradoresProps) {

  const queryClient = useQueryClient();

  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
  const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])

  const { control, handleSubmit } = useForm<TCompradoresEstoque>({
    defaultValues: valoresIniciais ? valoresIniciais : defaultValues,
  });

  //Querys e mutations
  const { mutate: criarComprador, isLoading: isLoadingRegister } =
    useMutation({
      mutationKey: "criar-comprador",
      mutationFn: (inputs: TCompradoresEstoque) =>
        axiosClient.post("estoque/configuracoes/criar-comprador", inputs),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao cadastrar comprador!", {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["pesquisar-compradores"]);
        handleClose();
        enqueueSnackbar("Comprador cadastrado com sucesso!", {
          variant: "success",
        });
      },
    });

  const { mutate: editarComprador, isLoading: isLoadingUpdate } =
    useMutation({
      mutationKey: "editar-departamento",
      mutationFn: (inputs: TCompradoresEstoque) =>
        axiosClient.put(
          `estoque/configuracoes/atualizar-comprador/${valoresIniciais?.id}`,
          inputs
        ),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao atualizar comprador!", {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["pesquisar-compradores"]);
        handleClose()
        enqueueSnackbar("Comprador atualizado com sucesso!", {
          variant: "success",
        });
      },
    });
  //

  const onSubmit = (data: TCompradoresEstoque) => {
    if (isEdit) editarComprador(data);
    if (!isEdit) criarComprador(data);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit ? "Editar" : "Cadastrar"} Comprador
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box px={0.5} my={1}>
              <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 6}}>
                  <Controller
                    name="nome"
                    control={control}
                    rules={{ required: "Nome é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Nome"
                        defaultValue={valoresIniciais?.nome}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                  <Controller
                    name="id"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Código"
                        type="number"
                        fullWidth
                        defaultValue={valoresIniciais?.id}
                        value={field.value}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Email"
                        type="email"
                        fullWidth
                        defaultValue={valoresIniciais?.email}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                  <Controller
                    name="telefone"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Telefone"
                        type="text"
                        fullWidth
                        defaultValue={valoresIniciais?.telefone}
                        value={field.value}
                        onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleClose}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              type="submit"
              disabled={isLoadingRegister || isLoadingUpdate}
            >
              {(isLoadingRegister || isLoadingUpdate) && (
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
              )}
              {isEdit ? "Atualizar" : "Cadastrar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

interface DialogCriacaoEditCompradoresProps {
  open: boolean;
  handleClose: () => void;
  defaultValue: TOpenModalComprador;
}

const defaultValues: TCompradoresEstoque = {
  id: 0,
  nome: "",
  email: "",
  telefone: "",
  fax: "",
};
